import { Injectable } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { addDaysToDateStruct, createDateString } from './calendar.utils';
import { CalendarDayInfo } from './calendar.component';

@Injectable({
    providedIn: 'root',
})
export class CalendarService {
    singleDate: NgbDate;
    fromDate: NgbDate;
    toDate: NgbDate;
    hoveredDate: NgbDate | null = null;
    isSingleDate = false;
    internalInfoForEachDay: Map<string, CalendarDayInfo> = new Map();

    resetProps() {
        this.singleDate = null;
        this.fromDate = null;
        this.toDate = null;
        this.hoveredDate = null;
        this.isSingleDate = false;
        this.internalInfoForEachDay = new Map();
    }

    disableDates() {
        this.internalInfoForEachDay.forEach((value) => {
            value.isDisabled = this.shouldDisableDate(NgbDate.from(value.date));
        });
    }

    // these operations get called a lot on the calendar. Make sure they are minimal
    isHovered(date: NgbDate) {
        return (
            !this.isSingleDate &&
            this.fromDate &&
            !this.toDate &&
            this.hoveredDate &&
            date.after(this.fromDate) &&
            (date.before(this.hoveredDate) || date.equals(this.hoveredDate))
        );
    }

    isHoveredDate(date: NgbDate) {
        return date.equals(this.hoveredDate);
    }

    isInside(date: NgbDate) {
        return (
            !this.isSingleDate &&
            this.toDate &&
            date.after(this.fromDate) &&
            date.before(this.toDate)
        );
    }

    isRange(date: NgbDate) {
        if (this.isSingleDate) return false;
        return (
            (this.fromDate && date.equals(this.fromDate)) ||
            (this.toDate && date.equals(this.toDate)) ||
            this.isInside(date) ||
            this.isHovered(date)
        );
    }

    isDateDisabled(date: NgbDate): boolean {
        const infoItem = this.internalInfoForEachDay.get(
            createDateString(date)
        );
        if (!infoItem) {
            return true;
        }
        return infoItem.isDisabled;
    }

    private shouldDisableDate(date: NgbDate): boolean {
        if (this.isFromOrToDate(date)) {
            return false;
        }

        const disabled = !this.internalInfoForEachDay.get(
            createDateString(date)
        );
        if (disabled) {
            return true;
        }

        const disabledBecauseOfRoomId = this.disableIfNotSameRoomId(date);
        if (disabledBecauseOfRoomId) {
            return true;
        }

        const disabledBecauseOfMinimumStay =
            this.disableIfMinimumStayNotPossible(date);
        if (disabledBecauseOfMinimumStay) {
            return true;
        }

        return disabled;
    }

    private isFromOrToDate(date: NgbDate): boolean {
        return (
            (this.fromDate && date.equals(this.fromDate)) ||
            (this.toDate && date.equals(this.toDate))
        );
    }

    private disableIfNotSameRoomId(date: NgbDate): boolean {
        if (!this.fromDate) {
            return false;
        }

        // exception for the last date because it doesn't actually need to have availability
        const isDateFromPlusMinimumStay =
            this.isDateFromDatePlusMinimumStay(date);
        if (isDateFromPlusMinimumStay) {
            return false;
        }
        const internalInfoItem = this.internalInfoForEachDay.get(
            createDateString(this.fromDate)
        );
        if (!internalInfoItem) {
            return false;
        }
        const { roomIds } = internalInfoItem;
        if (!roomIds) {
            return false;
        }
        const internalInfoItemForDate = this.internalInfoForEachDay.get(
            createDateString(date)
        );

        if (!internalInfoItemForDate) {
            return true;
        }
        return !roomIds.some((roomId) =>
            internalInfoItemForDate.roomIds?.includes(roomId)
        );
    }

    private disableIfMinimumStayNotPossible(date: NgbDate): boolean {
        // if this check is for a date while the fromDate is selected we don't care about the minimum stay not being
        // possible. In that case the from date selection will disable these. This is just for nothing selected state
        if (this.fromDate) {
            return false;
        }
        const internalInfoItem = this.internalInfoForEachDay.get(
            createDateString(date)
        );
        if (!internalInfoItem) {
            return true;
        }
        // return false if the date is fromDate + minLengthOfStay of fromDate because we need to be
        // able to choose this as an end date
        if (this.isDateFromDatePlusMinimumStay(date)) {
            return false;
        }

        const { minLengthOfStay } = internalInfoItem;
        for (let i = 1; i < minLengthOfStay; i++) {
            const dateToCheck = addDaysToDateStruct(date, i);
            if (
                !this.internalInfoForEachDay.get(createDateString(dateToCheck))
            ) {
                return true;
            }
        }
        return false;
    }

    private isDateFromDatePlusMinimumStay(date: NgbDate): boolean {
        if (!this.fromDate) {
            return false;
        }

        const { minLengthOfStay } =
            this.internalInfoForEachDay.get(createDateString(this.fromDate)) ??
            {};

        if (!minLengthOfStay) {
            return false;
        }

        return date.equals(addDaysToDateStruct(this.fromDate, minLengthOfStay));
    }
}
